/* eslint-disable no-param-reassign */

import { useState } from "react"

import { DeviceAttachment } from "../../../../../components/devices/types/providerTypes"
import { AppError } from "../../../../../errors/errorProcessors"
import { invalidApiDataError } from "../../../../../errors/recognisedErrors"
import {
    postClientProtected,
    postFormDataClientProtected,
} from "../../../clients/clients"
import attachmentUploadUrlMapper from "../../../mappers/registerDevice/getAttachmentUploadUrlsMapper"
import {
    TAttachmentUploadUrl,
    TAttachmentUploadUrlError,
    TAttachmentUploadUrlResponse,
    TAttachmentUploadUrlSuccess,
} from "../../../types/devices/ttr/getAttachmentUploadUrlsTypes"
import {
    GET_LCT_ATTACHMENT_UPLOAD_URLS,
    GET_TTR_ATTACHMENT_UPLOAD_URLS,
} from "../../../urls/private"

type AttachmentSummary = {
    fileName: string
    description: string
    private: boolean
    contentType: string
}

type GetUploadURLsPostBody = {
    deviceRef?: string
    attachment_uuid?: string
    attachment_summaries: AttachmentSummary[]
}

export type UploadAttachmentResult = {
    fileName: string
    result: "fulfilled" | "rejected"
    reason?: string
}

interface IUploadAttachment {
    uploadAttachments: (
        attachments: DeviceAttachment[]
    ) => Promise<UploadAttachmentResult[]>
    error?: AppError
    loading: boolean
}

/** Checks if a URL object response from the API is an error object */
const isAttachmentUploadUrlError = (
    attchmentUploadUrl: TAttachmentUploadUrl
): attchmentUploadUrl is TAttachmentUploadUrlError =>
    (attchmentUploadUrl as TAttachmentUploadUrlError).error_reason !== undefined

/** Creates a POST body for a /get-attachment-upload-urls request */
const createUploadUrlsPostBody = (
    attachments: DeviceAttachment[],
    deviceRef?: string,
    attachmentUUID?: string
): GetUploadURLsPostBody => ({
    deviceRef,
    attachment_uuid: attachmentUUID,
    attachment_summaries: attachments.map((attachement) => ({
        fileName: attachement.file.name,
        description: attachement.description ?? "",
        private: attachement.restricted,
        contentType: attachement.file.type,
    })),
})

/** Requests the S3 upload URLs for a list of attachments */
const getUploadUrls = async (
    postBody: GetUploadURLsPostBody,
    isLctAmendment: boolean
): Promise<TAttachmentUploadUrlResponse> => {
    const unmappedUploadUrlsResponse = await postClientProtected(
        postBody.deviceRef
            ? GET_TTR_ATTACHMENT_UPLOAD_URLS
            : GET_LCT_ATTACHMENT_UPLOAD_URLS(isLctAmendment),
        postBody
    )

    return attachmentUploadUrlMapper.map(unmappedUploadUrlsResponse)
}

/** Retrives a matching attachment for a given API URL object response, based on file name */
const getAttachmentForUploadUrl = (
    attachments: DeviceAttachment[],
    urlInfo: TAttachmentUploadUrl
): DeviceAttachment | undefined =>
    attachments.find(
        (attachement) => attachement.file.name === urlInfo.fileName
    )

/** Creates the form data for a S3 upload url */
const createFormDataForAttachment = (
    attachment: DeviceAttachment,
    urlInfo: TAttachmentUploadUrlSuccess
): FormData => {
    const formData = new FormData()

    Object.entries(urlInfo.form_fields).forEach(([key, value]) => {
        formData.append(key, value)
    })

    // This must be the last field added
    formData.append("file", attachment.file)

    return formData
}

/**
 * Hook for returning a function that can upload a list of device attachments,
 * also returns the loading and error states
 */
const useUploadDeviceAttachments = (
    deviceRef?: string,
    deviceId?: string,
    attachmentUUID?: string,
    isLctAmendment?: boolean
): IUploadAttachment => {
    const [error, setError] = useState<AppError>()
    const [loading, setLoading] = useState<boolean>(false)

    /** Returns a promise containing the result of an S3 attachment upload */
    const getUploadPromise = (
        urlInfo: TAttachmentUploadUrl,
        attachments: DeviceAttachment[]
    ): Promise<UploadAttachmentResult> => {
        const attachmentForUrl = getAttachmentForUploadUrl(attachments, urlInfo)

        if (!attachmentForUrl) {
            return Promise.resolve({
                fileName: urlInfo.fileName,
                result: "rejected",
                reason: "No attachment for provided URL",
            })
        }

        if (isAttachmentUploadUrlError(urlInfo)) {
            return Promise.resolve({
                fileName: urlInfo.fileName,
                result: "rejected",
                reason: urlInfo.error_reason,
            })
        }

        const formData = createFormDataForAttachment(attachmentForUrl, urlInfo)

        const s3UrlUploadPromise = postFormDataClientProtected(
            urlInfo.upload_url,
            formData
        )

        return s3UrlUploadPromise
            .then(() => {
                const result: UploadAttachmentResult = {
                    fileName: urlInfo.fileName,
                    result: "fulfilled",
                }
                return result
            })
            .catch((e: Error) => {
                const result: UploadAttachmentResult = {
                    fileName: urlInfo.fileName,
                    result: "rejected",
                    reason: e.message,
                }
                return result
            })
    }

    const failAllAttachments = (
        failedAttachments: DeviceAttachment[],
        reason: string
    ): UploadAttachmentResult[] =>
        failedAttachments.map((attachment) => ({
            fileName: attachment.file.name,
            result: "rejected",
            reason,
        }))

    /**
     * Takes a list of attachments and uploads them to S3.
     * Promise will reject if no upload URLs can be retrieved,
     * else it will resolve with a "fufilled" or "rejected" result
     */
    const uploadAttachments = async (
        attachments: DeviceAttachment[]
    ): Promise<UploadAttachmentResult[]> => {
        setLoading(true)

        if (!attachmentUUID && (!deviceRef || !deviceId)) {
            setLoading(false)
            return failAllAttachments(
                attachments,
                "Device reference not provided"
            )
        }

        const getUploadUrlsPostBody = createUploadUrlsPostBody(
            attachments,
            deviceRef,
            attachmentUUID
        )

        let uploadUrlsResponse: TAttachmentUploadUrlResponse

        try {
            uploadUrlsResponse = await getUploadUrls(
                getUploadUrlsPostBody,
                isLctAmendment === true
            )
        } catch {
            setError(invalidApiDataError("Failed to get upload URLs"))
            setLoading(false)
            return failAllAttachments(attachments, "Failed to get upload URL")
        }

        const s3UploadPromises = uploadUrlsResponse.urls.map((urlInfo) =>
            getUploadPromise(urlInfo, attachments)
        )

        const results = await Promise.all(s3UploadPromises)
        setLoading(false)

        return results
    }

    return {
        uploadAttachments,
        error,
        loading,
    }
}

export default useUploadDeviceAttachments
